import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import Header from '../components/common/Header';
import Footer from '../components/common/Footer';
import { initializeLucideIcons } from '../utils/initLucide';
import { collection, query, where, onSnapshot, getDocs, limit } from 'firebase/firestore';
import { generateAndDownloadVIPToken } from '../services/vipTokenGenerator';
import { db } from '../config/firebase';
import { COLLECTIONS } from '../utils/constants';
import '../styles/dashboard.css';

const UserDashboard = () => {
  const { currentUser, userProfile } = useAuth();
  const [loading, setLoading] = useState(true);
  const [userTokens, setUserTokens] = useState([]);
  const [generatingToken, setGeneratingToken] = useState(false);
  const [winnerDetails, setWinnerDetails] = useState(null);

  useEffect(() => {
    if (userProfile !== undefined) {
      setLoading(false);
    }
    initializeLucideIcons();
  }, [userProfile]);

  useEffect(() => {
    initializeLucideIcons();
  }, []);

  // Fetch user's VIP tokens
  useEffect(() => {
    if (!currentUser?.uid) return;

    let isMounted = true;

    // Try to fetch tokens by userId (currentUser.uid)
    const tokensQuery = query(
      collection(db, COLLECTIONS.VIP_TOKENS),
      where('userId', '==', currentUser.uid)
    );

    const unsubscribe = onSnapshot(tokensQuery, async (snapshot) => {
      let tokens = snapshot.docs
        .map(doc => ({
          id: doc.id,
          ...doc.data()
        }))
        .sort((a, b) => (b.generatedAt?.toMillis?.() || 0) - (a.generatedAt?.toMillis?.() || 0));

      console.log('Fetched tokens for userId:', currentUser.uid, 'Count:', tokens.length);

      // If no tokens found by userId, try by email
      if (tokens.length === 0 && currentUser?.email) {
        try {
          const emailQuery = query(
            collection(db, COLLECTIONS.VIP_TOKENS),
            where('userEmail', '==', currentUser.email)
          );
          const emailSnapshot = await getDocs(emailQuery);
          tokens = emailSnapshot.docs
            .map(doc => ({
              id: doc.id,
              ...doc.data()
            }))
            .sort((a, b) => (b.generatedAt?.toMillis?.() || 0) - (a.generatedAt?.toMillis?.() || 0));
          
          console.log('Fetched tokens by email:', currentUser.email, 'Count:', tokens.length);
        } catch (error) {
          console.error('Error fetching tokens by email:', error);
        }
      }

      if (isMounted) {
        setUserTokens(tokens);
        console.log('Final tokens set:', tokens.length, tokens);
      }
    });

    return () => {
      isMounted = false;
      unsubscribe();
    };
  }, [currentUser]);

  // Fetch winner details for current user
  useEffect(() => {
    if (!currentUser?.uid) return;

    const winnersQuery = query(
      collection(db, 'winners'),
      where('userId', '==', currentUser.uid)
    );

    const unsubscribe = onSnapshot(winnersQuery, (snapshot) => {
      if (!snapshot.empty) {
        const winnerData = snapshot.docs[0].data();
        setWinnerDetails({
          id: snapshot.docs[0].id,
          ...winnerData
        });
      }
    });

    return () => unsubscribe();
  }, [currentUser]);

  if (loading) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div className="loading-spinner">Loading...</div>
      </div>
    );
  }

  const getInitials = (name) => {
    if (!name) return 'U';
    return name.split(' ').map(n => n[0]).join('').toUpperCase().substring(0, 2);
  };

  if (!userProfile) {
    return (
      <div className="user-dashboard">
        <Header />
        <div className="dashboard-container" style={{
          minHeight: '70vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '2rem'
        }}>
          <div style={{
            background: 'white',
            borderRadius: '16px',
            padding: '2.5rem',
            maxWidth: '520px',
            width: '100%',
            textAlign: 'center',
            boxShadow: '0 20px 35px rgba(15, 23, 42, 0.15)'
          }}>
            <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>👋</div>
            <h2 style={{ marginBottom: '0.75rem', fontSize: '1.5rem', color: '#0f172a' }}>Welcome!</h2>
            <p style={{ color: '#475569', lineHeight: 1.6 }}>
              We couldn't find a complete profile for your account yet. Please finish your registration or contact support so we can set up your dashboard.
            </p>
            <p style={{ marginTop: '1rem', color: '#334155', fontWeight: 600 }}>{currentUser?.email}</p>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  // If user is not approved, show only approval message
  if (userProfile?.approvalStatus !== 'approved') {
    return (
      <div className="user-dashboard">
        <Header />
        
        <div className="dashboard-container" style={{ 
          minHeight: '80vh', 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center',
          padding: '2rem'
        }}>
          <div style={{
            background: 'linear-gradient(135deg, #fbbf24 0%, #f59e0b 100%)',
            color: 'white',
            padding: '3rem',
            borderRadius: '16px',
            textAlign: 'center',
            boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
            maxWidth: '600px',
            width: '100%'
          }}>
            <div style={{ fontSize: '5rem', marginBottom: '1.5rem' }}>⏳</div>
            <h2 style={{ fontSize: '2rem', fontWeight: 'bold', marginBottom: '1rem', color: 'white' }}>
              {userProfile?.approvalStatus === 'rejected' ? '❌ Account Rejected' : '⏳ Account Pending Approval'}
            </h2>
            <p style={{ fontSize: '1.1rem', opacity: 0.95, marginBottom: '1.5rem', lineHeight: '1.6' }}>
              {userProfile?.approvalStatus === 'rejected' 
                ? 'Your account has been rejected. Please contact support for more information.'
                : 'Your account is currently under review by our admin team. You will receive access to your dashboard once your account is approved.'}
            </p>
            <div style={{
              background: 'rgba(255, 255, 255, 0.2)',
              borderRadius: '12px',
              padding: '1.5rem',
              marginTop: '2rem'
            }}>
              <p style={{ fontSize: '0.95rem', color: 'white', marginBottom: '0.5rem' }}>
                <strong>📧 Email:</strong> {currentUser?.email}
              </p>
              <p style={{ fontSize: '0.95rem', color: 'white' }}>
                <strong>👤 Name:</strong> {userProfile?.name || 'N/A'}
              </p>
            </div>
            {userProfile?.approvalStatus === 'pending' && (
              <p style={{ fontSize: '0.875rem', opacity: 0.8, marginTop: '1.5rem', fontStyle: 'italic' }}>
                ⚡ This usually takes 24-48 hours. You'll be notified via email once approved.
              </p>
            )}
          </div>
        </div>

        <Footer />
      </div>
    );
  }

  const canGenerateToken = userTokens.length === 0;

  // Only show dashboard if approved
  return (
    <div className="user-dashboard">
      <Header />
      
      <div className="dashboard-container">
        {/* Profile Header */}
        <div className="dashboard-header">
          {userProfile?.profileImage ? (
            <img 
              src={userProfile.profileImage} 
              alt={userProfile.name}
              className="dashboard-profile-pic"
            />
          ) : (
            <div className="dashboard-profile-placeholder">
              {getInitials(userProfile?.name || currentUser?.displayName)}
            </div>
          )}
          <div className="dashboard-user-info">
            <h1>Welcome, {userProfile?.name || currentUser?.displayName || 'User'}!</h1>
            <p>{currentUser?.email}</p>
            <span className={`status-badge status-${userProfile?.approvalStatus || 'pending'}`}>
              {userProfile?.approvalStatus === 'approved' ? '✓ Approved Member' : 
               userProfile?.approvalStatus === 'rejected' ? '✗ Application Rejected' :
               '⏳ Pending Approval'}
            </span>
          </div>
        </div>

        {/* Stats Grid */}
        <div className="dashboard-stats">
          <div className="stat-card">
            <div className="stat-icon" style={{ background: '#dbeafe' }}>
              <i data-lucide="ticket" style={{ color: '#1e40af' }}></i>
            </div>
            <div className="stat-content">
              <h3>{userProfile?.totalTokens || 0}</h3>
              <p>VIP Tokens</p>
            </div>
          </div>

          <div className="stat-card">
            <div className="stat-icon" style={{ background: '#dcfce7' }}>
              <i data-lucide="trophy" style={{ color: '#15803d' }}></i>
            </div>
            <div className="stat-content">
              <h3>0</h3>
              <p>Prizes Won</p>
            </div>
          </div>

        </div>

        {/* Winner Prize Breakdown - Only show if user is a winner */}
        {winnerDetails && (
          <div className="dashboard-section" style={{ background: 'linear-gradient(135deg, #fbbf24 0%, #f59e0b 100%)', color: 'white', borderRadius: '12px', padding: '2rem', marginBottom: '2rem' }}>
            <h2 style={{ color: 'white', display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '1.5rem', fontSize: '1.5rem' }}>
              <i data-lucide="trophy" style={{ width: '32px', height: '32px' }}></i>
              🎉 Congratulations! You're a Winner!
            </h2>
            
            <div style={{ background: 'rgba(255,255,255,0.15)', backdropFilter: 'blur(10px)', borderRadius: '12px', padding: '1.5rem', border: '1px solid rgba(255,255,255,0.2)', marginBottom: '1rem' }}>
              <h3 style={{ color: 'white', fontSize: '1.1rem', fontWeight: 'bold', marginBottom: '0.5rem' }}>Prize Draw: {winnerDetails.prizeDrawTitle}</h3>
              <p style={{ color: 'rgba(255,255,255,0.9)', fontSize: '0.875rem' }}>Declared on: {winnerDetails.declaredAt?.toDate ? new Date(winnerDetails.declaredAt.toDate()).toLocaleDateString('en-IN') : 'Recently'}</p>
            </div>
            
            <div style={{ background: 'rgba(255,255,255,0.15)', backdropFilter: 'blur(10px)', borderRadius: '12px', padding: '1.5rem', border: '1px solid rgba(255,255,255,0.2)' }}>
              <h3 style={{ color: 'white', fontSize: '1rem', fontWeight: 'bold', marginBottom: '1rem' }}>Prize Distribution Breakdown</h3>
              
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem', marginBottom: '1rem' }}>
                <div style={{ background: 'rgba(255,255,255,0.2)', borderRadius: '8px', padding: '1rem', textAlign: 'center' }}>
                  <p style={{ color: 'rgba(255,255,255,0.9)', fontSize: '0.875rem', marginBottom: '0.5rem' }}>Original Prize Amount</p>
                  <p style={{ color: 'white', fontSize: '1.75rem', fontWeight: 'bold' }}>₹{(winnerDetails.originalPrize || 0).toLocaleString('en-IN')}</p>
                </div>
                
                <div style={{ background: 'rgba(255,255,255,0.2)', borderRadius: '8px', padding: '1rem', textAlign: 'center' }}>
                  <p style={{ color: 'rgba(255,255,255,0.9)', fontSize: '0.875rem', marginBottom: '0.5rem' }}>Army Fund Donation ({winnerDetails.armyFundPercentage}%)</p>
                  <p style={{ color: 'white', fontSize: '1.75rem', fontWeight: 'bold' }}>- ₹{(winnerDetails.armyFundAmount || 0).toLocaleString('en-IN')}</p>
                </div>
                
                <div style={{ background: 'linear-gradient(135deg, #22c55e 0%, #16a34a 100%)', borderRadius: '8px', padding: '1rem', textAlign: 'center', border: '2px solid white' }}>
                  <p style={{ color: 'rgba(255,255,255,0.9)', fontSize: '0.875rem', marginBottom: '0.5rem' }}>Final Amount Received</p>
                  <p style={{ color: 'white', fontSize: '1.75rem', fontWeight: 'bold' }}>₹{(winnerDetails.finalAmount || 0).toLocaleString('en-IN')}</p>
                </div>
              </div>
              
              <div style={{ background: 'rgba(34, 197, 94, 0.2)', borderRadius: '8px', padding: '1rem', marginTop: '1rem', border: '1px solid rgba(34, 197, 94, 0.3)' }}>
                <p style={{ color: 'white', fontSize: '0.875rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <span style={{ fontSize: '1.25rem' }}>🇮🇳</span>
                  <strong>Thank you for supporting Army Funds!</strong> Your contribution helps support our heroes who protect our nation.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Personal Information - Read only */}
        <div className="dashboard-section" style={{ maxWidth: '900px', margin: '0 auto 2rem' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.25rem' }}>
            <h2 style={{ margin: 0, fontSize: '1.25rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <i data-lucide="user" style={{ width: '20px', height: '20px' }}></i>
              Personal Information
            </h2>
            <span style={{ fontSize: '0.85rem', color: '#64748b', fontWeight: 600 }}>Contact support to update details</span>
          </div>
          
          <div style={{ 
            display: 'grid', 
            gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', 
            gap: '1rem',
            background: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)',
            padding: '1.5rem',
            borderRadius: '12px',
            border: '2px solid #e5e7eb'
          }}>
            {[
              { label: 'Full Name', value: userProfile?.name || 'N/A' },
              { label: 'Email', value: currentUser?.email || 'N/A' },
              { label: 'Phone', value: userProfile?.phone || 'N/A' },
              { label: 'UPI Number', value: userProfile?.upiNumber || 'N/A' },
              { label: 'Member Since', value: userProfile?.createdAt ? new Date(userProfile.createdAt.toDate()).toLocaleDateString('en-IN', { month: 'short', year: 'numeric' }) : 'N/A' },
              { label: 'Status', value: userProfile?.status === 'active' ? '✓ Active' : '✗ Inactive', color: userProfile?.status === 'active' ? '#10b981' : '#ef4444' },
              { label: 'Address', value: userProfile?.address || 'N/A', full: true }
            ].map((item, index) => (
              <div key={item.label} style={{ gridColumn: item.full ? 'span 2' : 'auto', display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
                <label style={{ fontSize: '0.75rem', fontWeight: '600', color: '#64748b', textTransform: 'uppercase' }}>{item.label}</label>
                <p style={{ margin: 0, color: item.color || '#1e3a5f', fontSize: '0.9rem', fontWeight: '500', wordBreak: 'break-word' }}>
                  {item.value}
                </p>
              </div>
            ))}
          </div>
        </div>

        {/* VIP Tokens Section - Only show if approved */}
        {userProfile?.approvalStatus === 'approved' && (
          <div className="dashboard-section">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem', flexWrap: 'wrap', gap: '1rem' }}>
              <div>
                <h2 style={{ margin: 0 }}>
                  <i data-lucide="award" style={{ width: '24px', height: '24px' }}></i>
                  My VIP Tokens
                </h2>
                <p style={{ margin: 0, fontSize: '0.8rem', color: '#64748b' }}>
                  {userTokens.length > 0 
                    ? 'You have already generated your VIP token. Download it anytime below.' 
                    : 'Each user can generate exactly one VIP token. Generate yours now!'}
                </p>
              </div>
              <button 
                onClick={async () => {
                  if (!userProfile) {
                    alert('⚠️ Profile not loaded. Please refresh the page.');
                    return;
                  }
                  
                  if (!canGenerateToken) {
                    alert('⚠️ You have already generated your VIP token. Each user can only generate one token.');
                    return;
                  }
                  
                  const profileForToken = {
                    ...userProfile,
                    name: userProfile?.name || currentUser?.displayName,
                    email: userProfile?.email || currentUser?.email,
                    id: currentUser.uid,
                    userId: currentUser.uid,
                    uid: currentUser.uid,
                  };
                  
                  setGeneratingToken(true);
                  try {
                    const checkQuery = query(
                      collection(db, COLLECTIONS.VIP_TOKENS),
                      where('userId', '==', currentUser.uid),
                      limit(1)
                    );
                    const checkSnapshot = await getDocs(checkQuery);
                    
                    if (!checkSnapshot.empty) {
                      alert('⚠️ You already have a VIP token. Each user can only generate one token.');
                      setGeneratingToken(false);
                      return;
                    }
                    
                    const result = await generateAndDownloadVIPToken(
                      profileForToken,
                      currentUser.email,
                      { uploadToStorage: false, saveRecord: true }
                    );
                    
                    if (result.success) {
                      alert('✅ Token generated and downloaded successfully! Your token will appear below in a moment.');
                      setTimeout(() => {
                        window.location.reload();
                      }, 1000);
                    } else if (result.errorCode === 'TOKEN_ALREADY_EXISTS') {
                      alert('⚠️ You already have a VIP token. Each user can only generate one token.');
                    } else {
                      alert('❌ Failed to generate token: ' + (result.message || 'Please try again.'));
                    }
                  } catch (error) {
                    console.error('Token generation error:', error);
                    alert('❌ Failed to generate token. Please try again.');
                  } finally {
                    setGeneratingToken(false);
                  }
                }}
                disabled={generatingToken || !canGenerateToken}
                style={{
                  padding: '0.75rem 1.5rem',
                  background: generatingToken || !canGenerateToken ? '#9ca3af' : 'linear-gradient(135deg, #7c3aed 0%, #a855f7 100%)',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  fontWeight: 'bold',
                  cursor: generatingToken || !canGenerateToken ? 'not-allowed' : 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  fontSize: '0.875rem'
                }}
              >
                <i data-lucide={generatingToken ? "loader" : "plus"} style={{ width: '16px', height: '16px' }}></i>
                {generatingToken ? 'Generating...' : canGenerateToken ? 'Generate Token' : 'Token Issued'}
              </button>
            </div>
            
            {userTokens.length > 0 ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                {userTokens.map((token, index) => (
                  <div key={token.id} style={{
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    borderRadius: '12px',
                    padding: '1rem',
                    color: 'white',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    boxShadow: '0 4px 12px rgba(102, 126, 234, 0.3)',
                    transition: 'transform 0.2s'
                  }}>
                    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
                      <h3 style={{ margin: 0, fontSize: '1rem', fontWeight: 'bold' }}>
                        {token.tokenNumber || `VIP Token #${index + 1}`}
                      </h3>
                      <p style={{ margin: 0, fontSize: '0.75rem', opacity: 0.9 }}>
                        <strong>Status:</strong> {token.status || 'Active'}
                      </p>
                      <p style={{ margin: 0, fontSize: '0.75rem', opacity: 0.9 }}>
                        <strong>Generated:</strong> {token.generatedAt?.toDate ? new Date(token.generatedAt.toDate()).toLocaleDateString('en-IN') : 'N/A'}
                      </p>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      <span style={{ 
                        padding: '0.25rem 0.75rem', 
                        background: 'rgba(16, 185, 129, 0.2)', 
                        borderRadius: '20px', 
                        fontSize: '0.75rem', 
                        fontWeight: 'bold',
                        border: '1px solid rgba(16, 185, 129, 0.3)'
                      }}>
                        Active
                      </span>
                      <button 
                        className="token-action-btn"
                        onClick={async () => {
                          try {
                            const profileForToken = {
                              ...userProfile,
                              name: userProfile?.name || currentUser?.displayName,
                              email: userProfile?.email || currentUser?.email,
                              id: currentUser.uid,
                              userId: currentUser.uid,
                              uid: currentUser.uid,
                            };
                            const result = await generateAndDownloadVIPToken(
                              profileForToken,
                              currentUser.email,
                              { uploadToStorage: false, saveRecord: false, tokenNumber: token.tokenNumber }
                            );
                            if (result.success) {
                              alert('✅ Token downloaded successfully!');
                            } else {
                              alert('❌ Failed to download token. Please try again.');
                            }
                          } catch (error) {
                            console.error('Download error:', error);
                            alert('❌ Failed to download token.');
                          }
                        }}
                        style={{
                          padding: '0.5rem 1rem',
                          background: 'rgba(255, 255, 255, 0.2)',
                          border: '1px solid rgba(255, 255, 255, 0.3)',
                          color: 'white',
                          borderRadius: '6px',
                          cursor: 'pointer',
                          fontWeight: '600',
                          fontSize: '0.75rem',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '0.25rem'
                        }}
                      >
                        <i data-lucide="download" style={{ width: '14px', height: '14px' }}></i>
                        <span className="btn-text">Download</span>
                      </button>
                      <button 
                        className="token-action-btn"
                        style={{
                          padding: '0.5rem 1rem',
                          background: 'rgba(255, 255, 255, 0.2)',
                          border: '1px solid rgba(255, 255, 255, 0.3)',
                          color: 'white',
                          borderRadius: '6px',
                          cursor: 'pointer',
                          fontWeight: '600',
                          fontSize: '0.75rem',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '0.25rem'
                        }}>
                        <i data-lucide="share-2" style={{ width: '14px', height: '14px' }}></i>
                        <span className="btn-text">Share</span>
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="empty-state">
                <div className="empty-state-icon">🎫</div>
                <h3>No VIP Tokens Yet</h3>
                <p>Click "Generate Token" above to create your first VIP token.</p>
              </div>
            )}
          </div>
        )}

        {/* Social Links */}
        {(userProfile?.instagram || userProfile?.facebook || userProfile?.youtube) && (
          <div className="dashboard-section">
            <h2>
              <i data-lucide="share-2" style={{ width: '24px', height: '24px' }}></i>
              Social Media
            </h2>
            <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
              {userProfile.instagram && (
                <a href={userProfile.instagram} target="_blank" rel="noopener noreferrer" style={{
                  padding: '0.75rem 1.5rem',
                  background: 'linear-gradient(135deg, #f09433 0%, #e6683c 25%, #dc2743 50%, #cc2366 75%, #bc1888 100%)',
                  color: 'white',
                  borderRadius: '8px',
                  textDecoration: 'none',
                  fontWeight: 600,
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem'
                }}>
                  <i data-lucide="instagram" style={{ width: '20px', height: '20px' }}></i>
                  Instagram
                </a>
              )}
              {userProfile.facebook && (
                <a href={userProfile.facebook} target="_blank" rel="noopener noreferrer" style={{
                  padding: '0.75rem 1.5rem',
                  background: '#1877f2',
                  color: 'white',
                  borderRadius: '8px',
                  textDecoration: 'none',
                  fontWeight: 600,
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem'
                }}>
                  <i data-lucide="facebook" style={{ width: '20px', height: '20px' }}></i>
                  Facebook
                </a>
              )}
              {userProfile.youtube && (
                <a href={userProfile.youtube} target="_blank" rel="noopener noreferrer" style={{
                  padding: '0.75rem 1.5rem',
                  background: '#ff0000',
                  color: 'white',
                  borderRadius: '8px',
                  textDecoration: 'none',
                  fontWeight: 600,
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem'
                }}>
                  <i data-lucide="youtube" style={{ width: '20px', height: '20px' }}></i>
                  YouTube
                </a>
              )}
            </div>
          </div>
        )}
      </div>

      <Footer />
    </div>
  );
};

export default UserDashboard;
